import React = require('react');

import { DomId, MonthMultiDayPicker }from '@tableau/self-service-schedules/lib/src/components/controls/MonthMultiDayPicker';
import { TimePicker }from '@tableau/self-service-schedules/lib/src/components/controls/TimePicker';
import { WeekDayPicker }from '@tableau/self-service-schedules/lib/src/components/controls/WeekDayPicker';
import { DayTypeChoice } from '@tableau/self-service-schedules/lib/src/components/panes/MonthlyRecurrencePane';
import { getDayOfMonthOptions, getWeekdayInfos,RecurrenceDaysOfTheWeek } from '@tableau/self-service-schedules/lib/src/components/Recurrence';
import { TimeHhMm } from '@tableau/self-service-schedules/lib/src/components/TimeHhMm';
import { DropdownButton, DropdownButtonType, ExpandoDirection, ExpandoWidget, List, ListItem, SingleSelectDropdown, TabStyles } from '@tableau/widgets';
import { style } from 'typestyle';

import { Messages } from '~src/codegen/Localize';
import { CachingPolicyType } from '~src/dataAcceleration/models/IDataFreshnessInfo';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import { dropdownListStyle } from '~src/settings/styles/CommonStyles';
import { ITimeZone } from '~src/userSettings/serverApi/types/ITimeZone';
import { IGetTimeZonesResult, TimeZones } from '~src/userSettings/services/TimeZones';

import { DataFreshnessPolicyConfigEditorProps, DataFreshnessPolicyState, DataFreshnessRecurrenceEditorState, DataFreshnessRecurrenceType, LAST_MONTH_DAY } from '../types/DataFreshnessTypes';
import { findDataFreshnessRecurrenceSummary } from '../utils/DataFreshnessUtils';

export class DataFreshnessRecurrenceEditor extends
        React.Component<DataFreshnessPolicyConfigEditorProps, DataFreshnessRecurrenceEditorState> {
    readonly monthDayNumOptions = getDayOfMonthOptions();
    constructor(props: DataFreshnessPolicyConfigEditorProps) {
        super(props);
        this.state = {
            ...props,
            expanded : true,
            time : props.dialogState.refreshTimeOfDay,
            recurrenceType : props.dialogState.refreshAtType,
            daysOfMonth : props.dialogState.refreshAtDaysOfMonth,
            daysOfWeek : props.dialogState.refreshAtDaysOfWeek,
            recurrenceSummary: undefined,
        };
    }

    public render(): React.ReactNode {
      return <ExpandoWidget
          direction={ExpandoDirection.Down}
          isExpanded={this.state.expanded}
          label={this.state.recurrenceSummary || this.updateRecurrenceSummary()}
          onCollapse={() => this.setState({expanded: false})}
          onExpand={() => this.setState({expanded: true})}
          testId='data-freshness-recurrence-editor'
          style={{ alignItems: 'stretch', marginLeft: '25px' }}>
          {this.getCommonRecurrenceComponent()}
          {this.state.recurrenceType === DataFreshnessRecurrenceType.Month &&
            this.getMonthDayPicker()}
          {this.state.recurrenceType === DataFreshnessRecurrenceType.Week &&
            this.getWeekDayPicker()}
          {this.getTimezonePicker()}
        </ExpandoWidget>;
    }

    private getTimezonePicker(): JSX.Element {
      return <>
        <div style={BentonLabelTextStyle}>{Messages.WorkbookDataFreshnessPolicy_Timezone()}</div>
        <div style={WeekDayPickerStyle}>
          <TimezonePicker {...this.props}/>
          </div>
        </>;
    }

    private getCommonRecurrenceComponent(): JSX.Element {
        return <div className={style({ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '80px' })}>
            <TimePicker
                caption={Messages.at()}
                testSelectId={'data-freshness-recurrence-time-picker'}
                onTimeChange={(time: TimeHhMm) => {
                  this.props.dialogState.refreshTimeOfDay = TimeHhMm.toNumber(time);
                  this.setState({time: TimeHhMm.toNumber(time)});
                  this.updateRecurrenceSummary();
                }}
                selectedValue={TimeHhMm.fromNumber(this.state.time)}
                style={TimePickerCssStyle}
                minuteInterval={15}/>
            <SingleSelectDropdown
                type={DropdownButtonType.Line}
                label={Messages.every()}
                testId={'data-freshness-recurrence-type-selector'}
                className={RecurrenceTypePickerStyle}
                list={<List items={this.getRecurrenceOptionItems()} style={{ maxHeight: '300px', overflow: 'auto' }} />}
                onChange={(recurrenceOption: DataFreshnessRecurrenceType) => {
                  this.setState({recurrenceType: recurrenceOption});
                  this.props.dialogState.refreshAtType = recurrenceOption;
                  this.props.validatePolicy(this.props.dialogState);
                  this.updateRecurrenceSummary();
                }}
                value={this.state.recurrenceType || DataFreshnessRecurrenceType.Day}/>
        </div>;
    }

    private getMonthDayPicker(): JSX.Element {
        return <div style={{marginLeft: '24px'}}>
          <DropdownButton
            aria-controls={DomId}
            testId={'data-freshness-recurrence-month-days'}
            children={this.getDaysOfMonthSummary(this.props.dialogState)}
            renderPopupContent={() => <MonthMultiDayPicker
            testId={'data-freshness-recurrence-monthday-picker'}
            monthDays={this.monthDayNumOptions}
            selectedDayIds={this.state.daysOfMonth.map((day: number) =>
              // MonthMultiDayPicker recognizes DayTypeChoice.Last only
              day === LAST_MONTH_DAY ? DayTypeChoice.Last : day)}
            onSelectionChanged={(days: number[]) => {
              this.mergeSelectedMonthDays(days);
              this.props.validatePolicy(this.props.dialogState);
              this.updateRecurrenceSummary();
            }}
            style={MonthMultiDayPickerCssStyle}/>}
            type={DropdownButtonType.Line}
            label={Messages.daysOfTheMonth()}
            style={{marginBottom: '18px'}}/>
        </div>;
    }

    private mergeSelectedMonthDays(days: number[]): void {
        var selectedDays = days;
        if (days.indexOf(DayTypeChoice.Last) === days.length - 1) {
            selectedDays = [DayTypeChoice.Last];
        } else if (days.indexOf(DayTypeChoice.Last) === 0) {
            selectedDays = days.slice(1);
        }
        selectedDays = selectedDays.sort((a,b) => a-b);
        this.setState({daysOfMonth: selectedDays});
        this.setSelectedDaysInProps(selectedDays);
    }

    /**
     * Replaces DayTypeChoice.Last with LAST_MONTH_DAY that is expected in the date freshness back-end code
     */
    private setSelectedDaysInProps(selectedDays: number[]) {
      this.props.dialogState.refreshAtDaysOfMonth = selectedDays.map((day: number) =>
        day === DayTypeChoice.Last ? LAST_MONTH_DAY : day
      );
    }

    private getWeekDayPicker(): JSX.Element {
        return <>
        <div style={BentonLabelTextStyle}>{Messages.daysOfTheWeek()}</div>
        <div style={WeekDayPickerStyle}>
            <WeekDayPicker
            weekDays={getWeekdayInfos()}
            testId={'data-freshness-recurrence-week-days'}
            selectedDayIds={this.state.daysOfWeek}
            allowMultipleDaysOfWeek={true}
            onSelectionChanged={(daysOfWeek: RecurrenceDaysOfTheWeek[]) => {
                this.props.dialogState.refreshAtDaysOfWeek = daysOfWeek;
                this.setState({daysOfWeek: daysOfWeek});
                this.props.validatePolicy(this.props.dialogState);
                this.updateRecurrenceSummary();
            }}/>
        </div>
        </>;
    }

    private getRecurrenceOptionItems(): ListItem[] {
      return [
        {value: DataFreshnessRecurrenceType.Day,
          content: Messages.dataFreshness_RecurrenceType_Day(),
          testId: 'data-freshness-recurrence-type-day-dropdown'},
        {value: DataFreshnessRecurrenceType.Week,
          content: Messages.dataFreshness_RecurrenceType_Week(),
          testId: 'data-freshness-recurrence-type-week-dropdown'},
        {value: DataFreshnessRecurrenceType.Month,
          content: Messages.dataFreshness_RecurrenceType_Month(),
          testId: 'data-freshness-recurrence-type-month-dropdown'},
      ];
    }

    private getDaysOfMonthSummary(dialogState: DataFreshnessPolicyState): JSX.Element {
      var summary: string;
      var style = MonthDaysSummaryStyle;
      if (dialogState.refreshAtDaysOfMonth === undefined || dialogState.refreshAtDaysOfMonth.length === 0) {
        summary = Messages.workbookDataFreshnessPolicy_Monthly_NoDaySelected();
        style = Object.assign({}, MonthDaysSummaryStyle);
        style.color = Typography.Color.PrimaryLight;
      } else if (dialogState.refreshAtDaysOfMonth.length === 1 &&
        dialogState.refreshAtDaysOfMonth[0] === LAST_MONTH_DAY) {
        summary = Messages.workbookDataFreshnessPolicy_MonthDay_Last();
      } else {
        summary = dialogState.refreshAtDaysOfMonth.join(' | ') || '';
      }

      return <div style={style}>{summary}</div>;
    }

    private updateRecurrenceSummary() {
      var recurrenceSummary = undefined;
      switch (this.props.dialogState.refreshAtType) {
        case DataFreshnessRecurrenceType.Day:
          recurrenceSummary = findDataFreshnessRecurrenceSummary(CachingPolicyType.REPEAT_DAILY,
            this.props.dialogState.refreshTimeOfDay, '' /* no daysStringCsv */);
          break;
        case DataFreshnessRecurrenceType.Week:
          recurrenceSummary = findDataFreshnessRecurrenceSummary(CachingPolicyType.REPEAT_WEEKLY,
            this.props.dialogState.refreshTimeOfDay, this.props.dialogState.refreshAtDaysOfWeek.join(','));
          break;
        case DataFreshnessRecurrenceType.Month:
          recurrenceSummary = findDataFreshnessRecurrenceSummary(CachingPolicyType.REPEAT_MONTHLY,
            this.props.dialogState.refreshTimeOfDay, this.props.dialogState.refreshAtDaysOfMonth.join(','));
          break;
      }
      this.setState({recurrenceSummary: recurrenceSummary});
      return recurrenceSummary;
    }
}

function setTimeZoneIdIfNeeded(props: DataFreshnessPolicyConfigEditorProps) {
  if (props.dialogState.refreshAtTimezone === undefined) {
    props.dialogState.refreshAtTimezone = getSessionManagementServiceInstance().userTimeZone();
  }
}

function TimezonePicker(props: DataFreshnessPolicyConfigEditorProps) {
  setTimeZoneIdIfNeeded(props);
  const [currentTimeZone, setCurrentTimeZone] = React.useState(props.dialogState.refreshAtTimezone.timeZoneId);

  const timeZonesListItems = useTimeZonesListItems();
  const timeZoneIdToTimeZone = new Map();
  timeZonesListItems.forEach((item: ListItem & {timeZone: ITimeZone}) => timeZoneIdToTimeZone.set(item.value, item.timeZone));
  return (
    <SingleSelectDropdown
      list={<List items={timeZonesListItems} className={dropdownListStyle} />}
      value={currentTimeZone}
      onChange={(timezoneId: string) =>{
        props.dialogState.refreshAtTimezone = timeZoneIdToTimeZone.get(timezoneId);
        setCurrentTimeZone(timezoneId);
      }}
      type={DropdownButtonType.Outline}
      label=''
      testId={'data-freshness-recurrence-editor-timezone-dropdown'}
    />
  );
}

function useTimeZonesListItems() {
  const [timeZonesListItems, setTimeZonesListItems] = React.useState<ListItem[]>([]);
  // fetching time zones
  React.useEffect(() => {
    let timezonesNeeded = true;
    if (timezonesNeeded) {
      TimeZones.getAll().then((result: IGetTimeZonesResult) => {
        setTimeZonesListItems(
          [].concat(result.curatedTimeZonesList).map((timeZone: ITimeZone) => ({
            content: timeZone.displayName,
            value: timeZone.timeZoneId,
            testId: 'data-freshness-timezoneId-'+timeZone.timeZoneId,
            timeZone: timeZone
          }))
          );
      });
      return () => {
        timezonesNeeded = false;
      };
    }
  }, []);

  return timeZonesListItems;
}

const {Typography} = TabStyles;
const BentonLabelTextStyle: React.CSSProperties = {
  display: 'block',
  boxSizing: 'border-box',
  position: 'relative',
  fontSize: Typography.Sizing.Default,
  fontWeight: 'normal',
  color: Typography.Color.Primary,
  lineHeight: '21px',
  marginLeft: '24px'
};

const MonthDaysSummaryStyle: React.CSSProperties = {
  display: 'block',
  boxSizing: 'border-box',
  position: 'relative',
  fontSize: Typography.Sizing.Default,
  fontWeight: 'normal',
  color: Typography.Color.Primary,
  lineHeight: '21px',
};

const WeekDayPickerStyle: React.CSSProperties = {textAlign: 'left', flex: '1 2 400px', marginLeft: '24px', marginBottom: '18px'};
const TimePickerCssStyle: React.CSSProperties = {flex: '1 2', marginTop: '18px', marginLeft: '24px', marginBottom: '18px'};
const RecurrenceTypePickerStyle = style({ marginTop: '18px', marginBottom: '18px', flex: '1 2 125px',});
const MonthMultiDayPickerCssStyle: React.CSSProperties = { padding: '2px', width: '232px', marginBottom: '18px'};
